import React, { useState, useEffect, useRef } from "react";
import Menubar from "../Menubar/Menubar";
import { Outlet } from "react-router-dom";
import { SlMinus, SlPlus } from "react-icons/sl";

const BlogElementor = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  // const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const savedState = localStorage.getItem("menubarOpen");

    if (savedState !== null) {
      return JSON.parse(savedState);
    }

    return window.innerWidth >= 1024;
  });

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);

      if (mobile) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const generateId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return (
      Math.random().toString(36).substring(2, 15) + Date.now().toString(36)
    );
  };

  const headingOptions = ["h2", "h3", "h4", "h5", "h6"];

  const [title, setTitle] = useState("");
  const [mainContent, setMainContent] = useState("");
  const [startDate] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // SEO META STATE
  const [slug, setSlug] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [keywords, setKeywords] = useState("");
  const [coverThumb, setCoverThumb] = useState(null);
  const coverInputRef = useRef(null);

  // Main Heading State
  const [mainTitle] = useState("");
  const [mainHeadingLevel] = useState("h2");

  // FIRST DYNAMIC SECTIONS STATE
  const [sections, setSections] = useState([
    {
      id: generateId(),
      title: "",
      headingLevel: "h2",
      blocks: [{ id: generateId(), text: "" }],
      image: null,
    },
  ]);

  const [extraSections, setExtraSections] = useState([
    {
      id: generateId(),
      title: "",
      headingLevel: "h2",
      blocks: [{ id: generateId(), text: "" }],
    },
  ]);

  const [, setActiveSectionId] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setActiveSectionId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "image/webp") {
      setImagePreview(URL.createObjectURL(file));
    } else {
      alert("Please upload a .webp image.");
    }
  };

  // ===== FIRST SECTION LOGIC =====
  const addSection = () => {
    const lastSection = sections[sections.length - 1];
    const lastLevel = lastSection.headingLevel;
    const lastNumber = parseInt(lastLevel.replace("h", ""));

    const newLevel = lastNumber === 6 ? "h6" : lastLevel;

    setSections([
      ...sections,
      {
        id: generateId(),
        title: "",
        headingLevel: newLevel,
        blocks: [{ id: generateId(), text: "" }],
        image: null,
      },
    ]);
  };

  const removeSection = (id) => {
    if (sections.length > 1) {
      setSections(sections.filter((section) => section.id !== id));
    }
  };

  const updateSection = (id, field, value) => {
    setSections(
      sections.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    );
  };

  const addBlock = (sectionId, blockId) => {
    setSections(
      sections.map((section) => {
        if (section.id !== sectionId) return section;
        const index = section.blocks.findIndex((b) => b.id === blockId);
        const newBlocks = [...section.blocks];
        newBlocks.splice(index + 1, 0, { id: generateId(), text: "" });
        return { ...section, blocks: newBlocks };
      }),
    );
  };

  const removeBlock = (sectionId, blockId) => {
    setSections(
      sections.map((section) => {
        if (section.id !== sectionId || section.blocks.length === 1)
          return section;
        return {
          ...section,
          blocks: section.blocks.filter((b) => b.id !== blockId),
        };
      }),
    );
  };

  const updateBlock = (sectionId, blockId, value) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              blocks: section.blocks.map((b) =>
                b.id === blockId ? { ...b, text: value } : b,
              ),
            }
          : section,
      ),
    );
  };

  // ===== EXTRA SECTION LOGIC =====
  const addExtraSection = () => {
    const lastSection = extraSections[extraSections.length - 1];
    const lastLevel = lastSection.headingLevel;
    const lastNumber = parseInt(lastLevel.replace("h", ""));

    const newLevel = lastNumber === 6 ? "h6" : lastLevel;

    setExtraSections([
      ...extraSections,
      {
        id: generateId(),
        title: "",
        headingLevel: newLevel,
        blocks: [{ id: generateId(), text: "" }],
      },
    ]);
  };

  const removeExtraSection = (id) => {
    if (extraSections.length > 1) {
      setExtraSections(extraSections.filter((s) => s.id !== id));
    }
  };

  const updateExtraSection = (id, field, value) => {
    setExtraSections(
      extraSections.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    );
  };

  const addExtraBlock = (sectionId, blockId) => {
    setExtraSections(
      extraSections.map((section) => {
        if (section.id !== sectionId) return section;
        const index = section.blocks.findIndex((b) => b.id === blockId);
        const newBlocks = [...section.blocks];
        newBlocks.splice(index + 1, 0, { id: generateId(), text: "" });
        return { ...section, blocks: newBlocks };
      }),
    );
  };

  const removeExtraBlock = (sectionId, blockId) => {
    setExtraSections(
      extraSections.map((section) => {
        if (section.id !== sectionId || section.blocks.length === 1)
          return section;
        return {
          ...section,
          blocks: section.blocks.filter((b) => b.id !== blockId),
        };
      }),
    );
  };

  const handleCoverThumb = (file) => {
    if (file && file.type === "image/webp") {
      setCoverThumb(URL.createObjectURL(file));
    } else {
      alert("Please upload a .webp image.");
    }
  };

  // ===== SAVE & CANCEL =====
  const handleSave = () => {
    const finalData = {
      blogTitle: title,
      publishDate: startDate,
      heroImage: imagePreview,
      mainHeading: {
        text: mainTitle,
        level: mainHeadingLevel,
      },
      contentSections: sections,
      extraSections: extraSections,
      seo: {
        slug,
        metaTitle,
        metaDescription,
        keywords,
        coverThumbnail: coverThumb,
      },
    };

    console.log("Saving Blog Data:", finalData);
    alert("Blog saved! Check console for data.");
  };

  const handleCancel = () => {
    if (window.confirm("Discard all changes?")) {
      window.location.reload();
    }
  };

  // ===== LOGGING TAGS =====
  useEffect(() => {
    console.log("Main Heading Tag:", mainHeadingLevel);
  }, [mainHeadingLevel]);

  useEffect(() => {
    sections.forEach((section) => {
      console.log(`Section ${section.id} Heading Tag:`, section.headingLevel);
    });
  }, [sections]);

  //h2 --h6 logic
  const getAllowedHeadings = (currentIndex, allSections, isExtra = false) => {
    if (!allSections || allSections.length === 0) {
      return headingOptions;
    }

    let prevNumber;

    if (isExtra && sections.length > 0) {
      const lastMainSection = sections[sections.length - 1];
      prevNumber = parseInt(lastMainSection.headingLevel.replace("h", ""));
    } else if (currentIndex > 0) {
      const prevLevel = allSections[currentIndex - 1]?.headingLevel;
      if (!prevLevel) return headingOptions;
      prevNumber = parseInt(prevLevel.replace("h", ""));
    } else {
      return headingOptions;
    }

    if (prevNumber === 6) return ["h6"];

    return headingOptions.filter((h) => {
      const currentNumber = parseInt(h.replace("h", ""));
      return currentNumber >= prevNumber;
    });
  };

  return (
    <div className="flex bg-[#F8F9FA]">
      <Menubar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        isMobile={isMobile}
      />

      <main
        className={`p-5 lg:p-6 transition-all duration-500 w-full ${
          isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"
        }`}
      >
        <Outlet />

        {/* Header */}
        <div className="flex justify-between gap-5 items-start lg:items-center">
          <div className="flex flex-col lg:flex-row gap-5 items-center">
            <p className="font-semibold text-xl text-gray-700 ml-10 lg:ml-0">
              Blog Elementor
            </p>
          </div>

          <div className="flex gap-2 items-center">
            <p className="font-semibold text-green-800 text-sm">
              Schedule Post:
            </p>

            <input
              type="date"
              name=""
              id=""
              className="border-b border-t-0 border-r-0 border-l-0 text-sm bg-transparent focus:border-black focus:outline-none focus:ring-0"
            />
          </div>
        </div>

        <div className="my-5">
          <div ref={wrapperRef} className="text-left">
            <div className="mb-8 md:mb-10 lg:mb-8">
              <h1 className="">
                <input
                  type="text"
                  placeholder="Enter Blog Title Here..."
                  value={title}
                  maxLength={90}
                  onChange={(e) => setTitle(e.target.value)}
                  className="text-xl md:text-3xl 2xl:text-4xl w-full bg-transparent capitalize border-t-0 border-l-0 border-r-0 border-b-2 border-gray-300 focus:border-b-black outline-none placeholder:text-gray-400 py-2 transition-all duration-300 focus:ring-0 focus:outline-none"
                />
              </h1>
              <p className="text-sm text-gray-500 mt-1">{title.length} / 90</p>

              <textarea
                placeholder="Enter Blog Content Here..."
                value={mainContent}
                maxLength={160}
                onChange={(e) => setMainContent(e.target.value)}
                rows={2}
                className="w-full bg-transparent resize-none text-black leading-relaxed max-w-3xl mt-4 border-t-0 border-l-0 border-r-0 border-b-2 border-gray-300  placeholder:text-gray-400
                                        focus:border-black outline-none transition-all duration-300 focus:ring-0 focus:outline-none"
              />
              <p className="text-sm text-gray-500 mt-1">
                {mainContent.length} / 160
              </p>
            </div>

            <div
              onClick={() => fileInputRef.current.click()}
              className="mt-8 w-full h-[280px] sm:h-[350px] md:h-[420px] lg:h-[450px] border-2 border-dashed border-gray-300 flex items-center justify-center bg-white cursor-pointer transition relative overflow-hidden"
            >
              {imagePreview ? (
                <>
                  <img
                    src={imagePreview}
                    alt="Hero"
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current.click();
                    }}
                    className="absolute bottom-4 right-4 
                              flex items-center gap-1
                              bg-white/30 backdrop-blur-md
                              text-[#0f2a28] text-sm font-medium 
                              px-4 py-1.5 rounded-full shadow-md 
                               transition"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <rect
                        x="2"
                        y="4"
                        width="16"
                        height="13"
                        rx="2"
                        stroke="#0f2a28"
                        strokeWidth="1.5"
                      />
                      <circle cx="6.5" cy="8.5" r="1.5" fill="#0f2a28" />
                      <path
                        d="M2 13L6 9L11 14L14 11L18 15"
                        stroke="#0f2a28"
                        strokeWidth="1.5"
                      />
                      <circle
                        cx="17"
                        cy="17"
                        r="6"
                        fill="#0f2a28"
                        stroke="#0f2a28"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M17 14V20M14 17H20"
                        stroke="#ebeae8"
                        strokeWidth="1.5"
                      />
                    </svg>
                    Replace
                  </button>
                </>
              ) : (
                <div className="flex flex-col items-center text-center px-4">
                  <svg
                    width="60"
                    height="60"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="mb-2"
                  >
                    <rect
                      x="2"
                      y="4"
                      width="16"
                      height="13"
                      rx="2"
                      stroke="black"
                      strokeWidth="1.5"
                    />
                    <circle cx="6.5" cy="8.5" r="1.5" fill="black" />
                    <path
                      d="M2 13L6 9L11 14L14 11L18 15"
                      stroke="black"
                      strokeWidth="1.5"
                    />
                    <circle
                      cx="17"
                      cy="17"
                      r="6"
                      fill="white"
                      stroke="black"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M17 14V20M14 17H20"
                      stroke="black"
                      strokeWidth="1.5"
                    />
                  </svg>
                  <p className="text-gray-700 font-medium">
                    Upload Webp image only
                  </p>
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                accept="image/webp"
              />
            </div>

            {/* ===== FIRST DYNAMIC SECTIONS ===== */}
            <div className="mt-10">
              {sections.map((section, index) => {
                return (
                  <div
                    key={section.id}
                    className="relative pb-5"
                    onClick={() => setActiveSectionId(section.id)}
                  >
                    <div className="px-5 py-8 text-left bg-[#FAFAFA] relative transition-all duration-200 border border-transparent hover:border-gray-400 hover:shadow-md">
                      <div className="absolute right-3 top-3 flex gap-1">
                        <button
                          onClick={addSection}
                          className="hover:scale-110 transition-transform bg-[#0f2a28] text-white rounded-full border border-[#0f2a28]"
                        >
                          <SlPlus size={23} />
                        </button>
                        {sections.length > 1 && (
                          <button
                            onClick={() => removeSection(section.id)}
                            className="hover:scale-110 transition-transform text-[#0f2a28]"
                          >
                            <SlMinus size={23} />
                          </button>
                        )}
                      </div>

                      <div className="flex flex-col gap-2 mb-2">
                        <input
                          type="text"
                          placeholder="Add Title..."
                          value={section.title}
                          onChange={(e) =>
                            updateSection(section.id, "title", e.target.value)
                          }
                          className="w-full bg-transparent border-t-0 border-l-0 border-r-0 border-b outline-none font-semibold text-[14px] md:text-xl lg:text-[16px] 2xl:text-2xl focus:ring-0 focus:border-black"
                        />
                        <select
                          value={section.headingLevel}
                          onChange={(e) =>
                            updateSection(
                              section.id,
                              "headingLevel",
                              e.target.value,
                            )
                          }
                          className="border border-gray-300 rounded px-1 py-1 text-xs w-fit focus:ring-0 focus:border-gray-500"
                        >
                          {getAllowedHeadings(index, sections).map((h) => (
                            <option key={h} value={h}>
                              {h.toUpperCase()}
                            </option>
                          ))}
                        </select>
                      </div>

                      {section.blocks.map((block) => (
                        <div key={block.id} className="relative mb-3">
                          <textarea
                            placeholder="Start writing..."
                            value={block.text}
                            onChange={(e) =>
                              updateBlock(section.id, block.id, e.target.value)
                            }
                            className="w-full bg-transparent outline-none text-gray-600 text-[14px] md:text-xl lg:text-[16px] 2xl:text-2xl min-h-[110px] resize-none p-2 transition-all border-b border-t-0 border-l-0 border-r-0 border-gray-400 hover:border-t hover:border-r hover:border-l hover:border-gray-400 focus:border-gray-400 focus:ring-0"
                          />

                          <div className="absolute right-0 top-2 pr-2 flex gap-2">
                            <button
                              onClick={() => addBlock(section.id, block.id)}
                              className="hover:scale-110 transition-transform"
                            >
                              <SlPlus size={17} />
                            </button>
                            {section.blocks.length > 1 && (
                              <button
                                onClick={() =>
                                  removeBlock(section.id, block.id)
                                }
                                className="hover:scale-110 transition-transform"
                              >
                                <SlMinus size={17} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ===== MIDDLE BANNER ===== */}
            <div className="mt-10 mb-12">
              <div className="bg-gradient-to-r from-[#0f2a28] to-[#0d1f1d] text-white px-8 md:px-16 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
                <div className="max-w-2xl">
                  <h2 className="text-2xl md:text-3xl mb-4">
                    Make this itinerary yours
                  </h2>
                  <p className="text-sm md:text-base leading-relaxed text-gray-200">
                    Each and every Zorba Stays trip is tailored exactly to who
                    you are and what you want to do. So tell us about yourself
                    and we’ll create something that’s entirely you.
                  </p>
                </div>
                <div>
                  <button className="bg-white text-black px-6 py-2 text-sm font-medium hover:bg-gray-200 transition">
                    Start Planning
                  </button>
                </div>
              </div>
            </div>

            {/* ===== SECOND DYNAMIC SECTION ===== */}
            {extraSections.map((section, index) => {
              return (
                <div key={section.id} className="relative pb-5">
                  <div className="px-5 py-8 text-left bg-[#FAFAFA] relative border border-transparent hover:border-gray-400 transition-all duration-200">
                    <div className="absolute right-3 top-3 flex gap-1">
                      <button
                        onClick={addExtraSection}
                        className="hover:scale-110 transition-transform bg-[#0f2a28] text-white rounded-full border border-[#0f2a28]"
                      >
                        <SlPlus size={23} />
                      </button>
                      {extraSections.length > 1 && (
                        <button
                          onClick={() => removeExtraSection(section.id)}
                          className="hover:scale-110 transition-transform text-[#0f2a28]"
                        >
                          <SlMinus size={23} />
                        </button>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 mb-2">
                      <input
                        type="text"
                        placeholder="Add Title..."
                        value={section.title}
                        onChange={(e) =>
                          updateExtraSection(
                            section.id,
                            "title",
                            e.target.value,
                          )
                        }
                        className="w-full bg-transparent border-t-0 border-l-0 border-r-0 border-b outline-none font-semibold text-[14px] md:text-xl lg:text-[16px] 2xl:text-2xl focus:ring-0 focus:border-black"
                      />
                      <select
                        value={section.headingLevel}
                        onChange={(e) =>
                          updateExtraSection(
                            section.id,
                            "headingLevel",
                            e.target.value,
                          )
                        }
                        className="border border-gray-300 rounded px-1 py-1 text-xs w-fit focus:ring-0 focus:border-gray-500"
                      >
                        {getAllowedHeadings(index, extraSections, true).map(
                          (h) => (
                            <option key={h} value={h}>
                              {h.toUpperCase()}
                            </option>
                          ),
                        )}
                      </select>
                    </div>

                    {section.blocks.map((block) => (
                      <div key={block.id} className="relative mb-3">
                        <textarea
                          placeholder="Start writing..."
                          value={block.text}
                          onChange={(e) =>
                            updateBlock(section.id, block.id, e.target.value)
                          }
                          className="w-full bg-transparent outline-none text-gray-600 text-[14px] md:text-xl lg:text-[16px] 2xl:text-2xl min-h-[110px] resize-none p-2 transition-all border-b border-t-0 border-l-0 border-r-0 border-gray-400 hover:border-t hover:border-r hover:border-l hover:border-gray-400 focus:border-gray-400 focus:ring-0"
                        />

                        <div className="absolute right-0 top-2 pr-2 flex gap-2">
                          <button
                            onClick={() => addExtraBlock(section.id, block.id)}
                            className="hover:scale-110 transition-transform"
                          >
                            <SlPlus size={17} />
                          </button>
                          {section.blocks.length > 1 && (
                            <button
                              onClick={() =>
                                removeExtraBlock(section.id, block.id)
                              }
                              className="hover:scale-110 transition-transform"
                            >
                              <SlMinus size={17} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* SEO META SECTION */}
            <div className="mt-14 border-t border-gray-400 pt-12">
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_450px] gap-10 items-center">
                <div>
                  <h3 className="text-lg font-semibold mb-6 text-left tracking-wide">
                    Meta Data
                  </h3>
                  <div className="grid grid-cols-2 gap-5 text-left">
                    <input
                      type="text"
                      placeholder="Slug"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      className="w-full bg-transparent border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-black focus:ring-0 focus:outline-none"
                    />
                    <input
                      type="text"
                      placeholder="Meta Title"
                      value={metaTitle}
                      onChange={(e) => setMetaTitle(e.target.value)}
                      className="w-full bg-transparent border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-black focus:ring-0 focus:outline-none"
                    />
                    <textarea
                      placeholder="Meta Description"
                      value={metaDescription}
                      onChange={(e) => setMetaDescription(e.target.value)}
                      rows={3}
                      className="w-full bg-transparent border border-gray-300 rounded-md px-3 py-2 outline-none resize-none focus:border-black focus:ring-0 focus:outline-none"
                    />
                    <textarea
                      type="text"
                      placeholder="Keywords"
                      value={keywords}
                      onChange={(e) => setKeywords(e.target.value)}
                      className="w-full bg-transparent border border-gray-300 rounded-md px-3 py-2 outline-none resize-none focus:border-black focus:ring-0 focus:outline-none"
                    />
                  </div>

                  <h3 className="text-lg font-semibold my-6 text-left tracking-wide">
                    Additional Data
                  </h3>
                  <div className="grid grid-cols-2 gap-5 text-left">
                    <input
                      type="date"
                      className="w-full bg-transparent border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-black focus:ring-0 focus:outline-none"
                    />
                    <input
                      type="text"
                      placeholder="Read Time"
                      className="w-full bg-transparent border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-black focus:ring-0 focus:outline-none"
                    />
                    <textarea
                      placeholder="Location"
                      rows={2}
                      className="w-full bg-transparent border border-gray-300 rounded-md px-3 py-2 outline-none resize-none focus:border-black focus:ring-0 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="w-full flex flex-col">
                  <input
                    ref={coverInputRef}
                    type="file"
                    id="cover-upload"
                    className="hidden"
                    accept="image/webp"
                    onChange={(e) => handleCoverThumb(e.target.files[0])}
                  />

                  <div
                    onClick={() => coverInputRef.current.click()}
                    className="w-full h-[300px] border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center bg-white cursor-pointer transition relative overflow-hidden"
                  >
                    {coverThumb ? (
                      <img
                        src={coverThumb}
                        alt="Cover Thumbnail"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center text-center px-4">
                        <svg
                          width="50"
                          height="50"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="mb-2"
                        >
                          <rect
                            x="2"
                            y="4"
                            width="16"
                            height="13"
                            rx="2"
                            stroke="black"
                            strokeWidth="1.5"
                          />
                          <circle cx="6.5" cy="8.5" r="1.5" fill="black" />
                          <path
                            d="M2 13L6 9L11 14L14 11L18 15"
                            stroke="black"
                            strokeWidth="1.5"
                          />
                          <circle
                            cx="17"
                            cy="17"
                            r="6"
                            fill="white"
                            stroke="black"
                            strokeWidth="1.5"
                          />
                          <path
                            d="M17 14V20M14 17H20"
                            stroke="black"
                            strokeWidth="1.5"
                          />
                        </svg>
                        <p className="text-gray-600 text-sm font-medium">
                          Upload Cover Thumbnail (webp)
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end mt-3">
                    <button
                      type="button"
                      onClick={() => coverInputRef.current.click()}
                      className="flex items-center gap-2 border border-gray-300 px-4 py-1 rounded-lg shadow-sm hover:bg-gray-50 transition-all"
                    >
                      <svg
                        width="21"
                        height="21"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <rect
                          x="2"
                          y="4"
                          width="16"
                          height="13"
                          rx="2"
                          stroke="black"
                          strokeWidth="1.5"
                        />
                        <circle cx="6.5" cy="8.5" r="1.5" fill="black" />
                        <path
                          d="M2 13L6 9L11 14L14 11L18 15"
                          stroke="black"
                          strokeWidth="1.5"
                        />
                        <circle
                          cx="17"
                          cy="17"
                          r="6"
                          fill="white"
                          stroke="black"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M17 14V20M14 17H20"
                          stroke="black"
                          strokeWidth="1.5"
                        />
                      </svg>

                      <span className="text-sm font-semibold">
                        {coverThumb ? "Replace" : "Add"}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* BUTTONS */}
            <div className="grid grid-cols-2 gap-3 mt-8 w-80 justify-self-center">
              <button
                onClick={handleCancel}
                className="px-6 z-30 py-2 bg-gray-800 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-gray-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
              >
                Cancel
              </button>

              <button
                onClick={handleSave}
                className="px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BlogElementor;

// import React, { useState, useEffect, useRef } from "react";
// import Menubar from "../Menubar/Menubar";
// import { Outlet } from "react-router-dom";
// import { SlMinus, SlPlus } from "react-icons/sl";

// const BlogElementor = () => {
//   const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
//   const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);

//   useEffect(() => {
//     const handleResize = () => {
//       const mobile = window.innerWidth < 1024;
//       setIsMobile(mobile);

//       if (mobile) {
//         setIsSidebarOpen(false);
//       }
//     };

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   const generateId = () => {
//     if (typeof crypto !== "undefined" && crypto.randomUUID) {
//       return crypto.randomUUID();
//     }
//     return (
//       Math.random().toString(36).substring(2, 15) + Date.now().toString(36)
//     );
//   };

//   const headingOptions = ["h1", "h2", "h3", "h4", "h5", "h6"];

//   const [title, setTitle] = useState("");
//   const [mainContent, setMainContent] = useState("");
//   const [startDate] = useState(null);
//   const [imagePreview, setImagePreview] = useState(null);
//   const fileInputRef = useRef(null);

//   // SEO META STATE
//   const [slug, setSlug] = useState("");
//   const [metaTitle, setMetaTitle] = useState("");
//   const [metaDescription, setMetaDescription] = useState("");
//   const [keywords, setKeywords] = useState("");
//   const [coverThumb, setCoverThumb] = useState(null);
//   const coverInputRef = useRef(null);

//   // Main Heading State
//   const [mainTitle] = useState("");
//   const [mainHeadingLevel] = useState("h2");

//   // FIRST DYNAMIC SECTIONS STATE
//   const [sections, setSections] = useState([
//     {
//       id: generateId(),
//       title: "",
//       headingLevel: "h2",
//       blocks: [{ id: generateId(), text: "" }],
//       image: null,
//     },
//   ]);

//   const [extraSections, setExtraSections] = useState([
//     {
//       id: generateId(),
//       title: "",
//       headingLevel: "h2",
//       blocks: [{ id: generateId(), text: "" }],
//     },
//   ]);

//   const [activeSectionId, setActiveSectionId] = useState(null);
//   const wrapperRef = useRef(null);

//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
//         setActiveSectionId(null);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const handleFileChange = (e) => {
//     const file = e.target.files[0];
//     if (file && file.type === "image/webp") {
//       setImagePreview(URL.createObjectURL(file));
//     } else {
//       alert("Please upload a .webp image.");
//     }
//   };

//   // ===== FIRST SECTION LOGIC =====
//   const addSection = () => {
//     setSections([
//       ...sections,
//       {
//         id: generateId(),
//         title: "",
//         headingLevel: "h2",
//         blocks: [{ id: generateId(), text: "" }],
//         image: null,
//       },
//     ]);
//   };

//   const removeSection = (id) => {
//     if (sections.length > 1) {
//       setSections(sections.filter((section) => section.id !== id));
//     }
//   };

//   const updateSection = (id, field, value) => {
//     setSections(
//       sections.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
//     );
//   };

//   const addBlock = (sectionId, blockId) => {
//     setSections(
//       sections.map((section) => {
//         if (section.id !== sectionId) return section;
//         const index = section.blocks.findIndex((b) => b.id === blockId);
//         const newBlocks = [...section.blocks];
//         newBlocks.splice(index + 1, 0, { id: generateId(), text: "" });
//         return { ...section, blocks: newBlocks };
//       }),
//     );
//   };

//   const removeBlock = (sectionId, blockId) => {
//     setSections(
//       sections.map((section) => {
//         if (section.id !== sectionId || section.blocks.length === 1)
//           return section;
//         return {
//           ...section,
//           blocks: section.blocks.filter((b) => b.id !== blockId),
//         };
//       }),
//     );
//   };

//   const updateBlock = (sectionId, blockId, value) => {
//     setSections(
//       sections.map((section) =>
//         section.id === sectionId
//           ? {
//               ...section,
//               blocks: section.blocks.map((b) =>
//                 b.id === blockId ? { ...b, text: value } : b,
//               ),
//             }
//           : section,
//       ),
//     );
//   };

//   // ===== EXTRA SECTION LOGIC =====
//   const addExtraSection = () => {
//     setExtraSections([
//       ...extraSections,
//       {
//         id: generateId(),
//         title: "",
//         headingLevel: "h2", // Initialize heading level
//         blocks: [{ id: generateId(), text: "" }],
//       },
//     ]);
//   };

//   const removeExtraSection = (id) => {
//     if (extraSections.length > 1) {
//       setExtraSections(extraSections.filter((s) => s.id !== id));
//     }
//   };

//   const updateExtraSection = (id, field, value) => {
//     setExtraSections(
//       extraSections.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
//     );
//   };

//   const addExtraBlock = (sectionId, blockId) => {
//     setExtraSections(
//       extraSections.map((section) => {
//         if (section.id !== sectionId) return section;
//         const index = section.blocks.findIndex((b) => b.id === blockId);
//         const newBlocks = [...section.blocks];
//         newBlocks.splice(index + 1, 0, { id: generateId(), text: "" });
//         return { ...section, blocks: newBlocks };
//       }),
//     );
//   };

//   const removeExtraBlock = (sectionId, blockId) => {
//     setExtraSections(
//       extraSections.map((section) => {
//         if (section.id !== sectionId || section.blocks.length === 1)
//           return section;
//         return {
//           ...section,
//           blocks: section.blocks.filter((b) => b.id !== blockId),
//         };
//       }),
//     );
//   };

//   const updateExtraBlock = (sectionId, blockId, value) => {
//     setExtraSections(
//       extraSections.map((section) =>
//         section.id === sectionId
//           ? {
//               ...section,
//               blocks: section.blocks.map((b) =>
//                 b.id === blockId ? { ...b, text: value } : b,
//               ),
//             }
//           : section,
//       ),
//     );
//   };

//   const handleCoverThumb = (file) => {
//     if (file && file.type === "image/webp") {
//       setCoverThumb(URL.createObjectURL(file));
//     } else {
//       alert("Please upload a .webp image.");
//     }
//   };

//   // ===== SAVE & CANCEL =====
//   const handleSave = () => {
//     const finalData = {
//       blogTitle: title,
//       publishDate: startDate,
//       heroImage: imagePreview,
//       mainHeading: {
//         text: mainTitle,
//         level: mainHeadingLevel,
//       },
//       contentSections: sections,
//       extraSections: extraSections,
//       seo: {
//         slug,
//         metaTitle,
//         metaDescription,
//         keywords,
//         coverThumbnail: coverThumb,
//       },
//     };

//     console.log("Saving Blog Data:", finalData);
//     alert("Blog saved! Check console for data.");
//   };

//   const handleCancel = () => {
//     if (window.confirm("Discard all changes?")) {
//       window.location.reload();
//     }
//   };

//   // ===== LOGGING TAGS =====
//   useEffect(() => {
//     console.log("Main Heading Tag:", mainHeadingLevel);
//   }, [mainHeadingLevel]);

//   useEffect(() => {
//     sections.forEach((section) => {
//       console.log(`Section ${section.id} Heading Tag:`, section.headingLevel);
//     });
//   }, [sections]);

//   return (
//     <div className="flex bg-[#F8F9FA]">
//       <Menubar
//         isOpen={isSidebarOpen}
//         setIsOpen={setIsSidebarOpen}
//         isMobile={isMobile}
//       />

//       <main
//         className={`p-5 lg:p-6 transition-all duration-500 w-full ${
//           isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"
//         }`}
//       >
//         <Outlet />

//         {/* Header */}
//         <div className="flex justify-between gap-5 items-start lg:items-center">
//           <div className="flex flex-col lg:flex-row gap-5 items-center">
//             <p className="font-semibold text-xl text-gray-700 ml-10 lg:ml-0">
//               Blog Elementor
//             </p>
//           </div>

//           <div className="flex gap-2 items-center">
//             <p className="font-semibold text-green-800 text-sm">
//               Schedule Post:
//             </p>

//             <input
//               type="date"
//               name=""
//               id=""
//               className="border-b border-t-0 border-r-0 border-l-0 text-sm bg-transparent focus:border-black focus:outline-none focus:ring-0"
//             />
//           </div>
//         </div>

//         <div className="my-5">
//           <div ref={wrapperRef} className="text-left">
//             <div className="mb-8 md:mb-10 lg:mb-8">
//               <h1 className="">
//                 <input
//                   type="text"
//                   placeholder="Enter Blog Title Here..."
//                   value={title}
//                   maxLength={90}
//                   onChange={(e) => setTitle(e.target.value)}
//                   className="text-xl md:text-3xl 2xl:text-4xl w-full bg-transparent capitalize border-t-0 border-l-0 border-r-0 border-b-2 border-gray-300 focus:border-b-black outline-none placeholder:text-gray-400 py-2 transition-all duration-300 focus:ring-0 focus:outline-none"
//                 />
//               </h1>
//               <p className="text-sm text-gray-500 mt-1">{title.length} / 90</p>

//               <textarea
//                 placeholder="Enter Blog Content Here..."
//                 value={mainContent}
//                 maxLength={160}
//                 onChange={(e) => setMainContent(e.target.value)}
//                 rows={2}
//                 className="w-full bg-transparent resize-none text-black leading-relaxed max-w-3xl mt-4 border-t-0 border-l-0 border-r-0 border-b-2 border-gray-300  placeholder:text-gray-400
//                                         focus:border-black outline-none transition-all duration-300 focus:ring-0 focus:outline-none"
//               />
//               <p className="text-sm text-gray-500 mt-1">
//                 {mainContent.length} / 160
//               </p>
//             </div>

//             <div
//               onClick={() => fileInputRef.current.click()}
//               className="mt-8 w-full h-[280px] sm:h-[350px] md:h-[420px] lg:h-[450px] border-2 border-dashed border-gray-300 flex items-center justify-center bg-white cursor-pointer transition relative overflow-hidden"
//             >
//               {imagePreview ? (
//                 <img
//                   src={imagePreview}
//                   alt="Hero"
//                   className="w-full h-full object-cover"
//                 />
//               ) : (
//                 <div className="flex flex-col items-center text-center px-4">
//                   <svg
//                     width="60"
//                     height="60"
//                     viewBox="0 0 24 24"
//                     fill="none"
//                     className="mb-2"
//                   >
//                     <rect
//                       x="2"
//                       y="4"
//                       width="16"
//                       height="13"
//                       rx="2"
//                       stroke="black"
//                       strokeWidth="1.5"
//                     />
//                     <circle cx="6.5" cy="8.5" r="1.5" fill="black" />
//                     <path
//                       d="M2 13L6 9L11 14L14 11L18 15"
//                       stroke="black"
//                       strokeWidth="1.5"
//                     />
//                     <circle
//                       cx="17"
//                       cy="17"
//                       r="6"
//                       fill="white"
//                       stroke="black"
//                       strokeWidth="1.5"
//                     />
//                     <path
//                       d="M17 14V20M14 17H20"
//                       stroke="black"
//                       strokeWidth="1.5"
//                     />
//                   </svg>
//                   <p className="text-gray-700 font-medium">
//                     Upload Webp image only
//                   </p>
//                 </div>
//               )}
//               <input
//                 type="file"
//                 ref={fileInputRef}
//                 onChange={handleFileChange}
//                 className="hidden"
//                 accept="image/webp"
//               />
//             </div>

//             {/* ===== FIRST DYNAMIC SECTIONS ===== */}
//             <div className="mt-10">
//               {sections.map((section) => {
//                 const isActive = activeSectionId === section.id;
//                 return (
//                   <div
//                     key={section.id}
//                     className="relative pb-5"
//                     onClick={() => setActiveSectionId(section.id)}
//                   >
//                     <div
//                       className={`px-5 py-8 text-left bg-[#FAFAFA] relative transition-all duration-200 ${isActive ? "border border-gray-400 shadow-md" : "border border-transparent"}`}
//                     >
//                       <div className="absolute right-3 top-3 flex gap-1">
//                         <button
//                           onClick={addSection}
//                           className="hover:scale-110 transition-transform bg-[#0f2a28] text-white rounded-full border border-[#0f2a28]"
//                         >
//                           <SlPlus size={23} />
//                         </button>
//                         {sections.length > 1 && (
//                           <button
//                             onClick={() => removeSection(section.id)}
//                             className="hover:scale-110 transition-transform text-[#0f2a28]"
//                           >
//                             <SlMinus size={23} />
//                           </button>
//                         )}
//                       </div>

//                       <div className="flex flex-col gap-2 mb-2">
//                         <input
//                           type="text"
//                           placeholder="Add Title..."
//                           value={section.title}
//                           onChange={(e) =>
//                             updateSection(section.id, "title", e.target.value)
//                           }
//                           className="w-full bg-transparent border-t-0 border-l-0 border-r-0 border-b outline-none font-semibold text-[14px] md:text-xl lg:text-[16px] 2xl:text-2xl focus:ring-0 focus:border-black"
//                         />
//                         <select
//                           value={section.headingLevel}
//                           onChange={(e) =>
//                             updateSection(
//                               section.id,
//                               "headingLevel",
//                               e.target.value,
//                             )
//                           }
//                           className="border border-gray-300 rounded px-1 py-1 text-xs w-fit focus:ring-0 focus:border-gray-500"
//                         >
//                           {headingOptions.map((h) => (
//                             <option key={h} value={h}>
//                               {h.toUpperCase()}
//                             </option>
//                           ))}
//                         </select>
//                       </div>

//                       {section.blocks.map((block) => (
//                         <div key={block.id} className="relative mb-3">
//                           <textarea
//                             placeholder="Start writing..."
//                             value={block.text}
//                             onChange={(e) =>
//                               updateBlock(section.id, block.id, e.target.value)
//                             }
//                             className="w-full bg-transparent outline-none text-gray-600 text-[14px] md:text-xl lg:text-[16px] 2xl:text-2xl min-h-[110px] resize-none p-2 transition-all border-b border-t-0 border-l-0 border-r-0 border-gray-400 focus:border-t focus:border-r focus:border-l focus:border-black focus:ring-0"
//                           />
//                           <div className="absolute right-0 top-2 pr-2 flex gap-2">
//                             <button
//                               onClick={() => addBlock(section.id, block.id)}
//                               className="hover:scale-110 transition-transform"
//                             >
//                               <SlPlus size={17} />
//                             </button>
//                             {section.blocks.length > 1 && (
//                               <button
//                                 onClick={() =>
//                                   removeBlock(section.id, block.id)
//                                 }
//                                 className="hover:scale-110 transition-transform"
//                               >
//                                 <SlMinus size={17} />
//                               </button>
//                             )}
//                           </div>
//                         </div>
//                       ))}
//                     </div>
//                   </div>
//                 );
//               })}
//             </div>

//             {/* ===== MIDDLE BANNER ===== */}
//             <div className="mt-10 mb-12">
//               <div className="bg-gradient-to-r from-[#0f2a28] to-[#0d1f1d] text-white px-8 md:px-16 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
//                 <div className="max-w-2xl">
//                   <h2 className="text-2xl md:text-3xl mb-4">
//                     Make this itinerary yours
//                   </h2>
//                   <p className="text-sm md:text-base leading-relaxed text-gray-200">
//                     Each and every Zorba Stays trip is tailored exactly to who
//                     you are and what you want to do. So tell us about yourself
//                     and we’ll create something that’s entirely you.
//                   </p>
//                 </div>
//                 <div>
//                   <button className="bg-white text-black px-6 py-2 text-sm font-medium hover:bg-gray-200 transition">
//                     Start Planning
//                   </button>
//                 </div>
//               </div>
//             </div>

//             {/* ===== SECOND DYNAMIC SECTION ===== */}
//             {extraSections.map((section) => {
//               return (
//                 <div key={section.id} className="relative pb-5">
//                   <div className="px-5 py-8 text-left bg-[#FAFAFA] relative border border-transparent hover:border-gray-400 transition-all duration-200">
//                     <div className="absolute right-3 top-3 flex gap-1">
//                       <button
//                         onClick={addExtraSection}
//                         className="hover:scale-110 transition-transform bg-[#0f2a28] text-white rounded-full border border-[#0f2a28]"
//                       >
//                         <SlPlus size={23} />
//                       </button>
//                       {extraSections.length > 1 && (
//                         <button
//                           onClick={() => removeExtraSection(section.id)}
//                           className="hover:scale-110 transition-transform text-[#0f2a28]"
//                         >
//                           <SlMinus size={23} />
//                         </button>
//                       )}
//                     </div>

//                     <div className="flex flex-col gap-2 mb-2">
//                       <input
//                         type="text"
//                         placeholder="Add Title..."
//                         value={section.title}
//                         onChange={(e) =>
//                           updateExtraSection(
//                             section.id,
//                             "title",
//                             e.target.value,
//                           )
//                         }
//                         className="w-full bg-transparent border-t-0 border-l-0 border-r-0 border-b outline-none font-semibold text-[14px] md:text-xl lg:text-[16px] 2xl:text-2xl focus:ring-0 focus:border-black"
//                       />
//                       <select
//                         value={section.headingLevel}
//                         onChange={(e) =>
//                           updateExtraSection(
//                             section.id,
//                             "headingLevel",
//                             e.target.value,
//                           )
//                         }
//                         className="border border-gray-300 rounded px-1 py-1 text-xs w-fit focus:ring-0 focus:border-gray-500"
//                       >
//                         {headingOptions.map((h) => (
//                           <option key={h} value={h}>
//                             {h.toUpperCase()}
//                           </option>
//                         ))}
//                       </select>
//                     </div>

//                     {section.blocks.map((block) => (
//                       <div key={block.id} className="relative mb-3">
//                         <textarea
//                           placeholder="Start writing..."
//                           value={block.text}
//                           onChange={(e) =>
//                             updateExtraBlock(
//                               section.id,
//                               block.id,
//                               e.target.value,
//                             )
//                           }
//                           className="w-full bg-transparent outline-none text-gray-600 text-[14px] md:text-xl lg:text-[16px] 2xl:text-2xl min-h-[110px] resize-none p-2 transition-all border-b border-t-0 border-l-0 border-r-0 border-gray-400 focus:border-t focus:border-r focus:border-l focus:border-black focus:ring-0"
//                         />
//                         <div className="absolute right-0 top-2 pr-2 flex gap-2">
//                           <button
//                             onClick={() => addExtraBlock(section.id, block.id)}
//                             className="hover:scale-110 transition-transform"
//                           >
//                             <SlPlus size={17} />
//                           </button>
//                           {section.blocks.length > 1 && (
//                             <button
//                               onClick={() =>
//                                 removeExtraBlock(section.id, block.id)
//                               }
//                               className="hover:scale-110 transition-transform"
//                             >
//                               <SlMinus size={17} />
//                             </button>
//                           )}
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 </div>
//               );
//             })}

//             {/* SEO META SECTION */}
//             <div className="mt-14 border-t border-gray-400 pt-12">
//               <div className="grid grid-cols-1 lg:grid-cols-[1fr_450px] gap-10 items-center">
//                 <div>
//                   <h3 className="text-lg font-semibold mb-6 text-left tracking-wide">
//                     Meta Data
//                   </h3>
//                   <div className="grid grid-cols-2 gap-5 text-left">
//                     <input
//                       type="text"
//                       placeholder="Slug"
//                       value={slug}
//                       onChange={(e) => setSlug(e.target.value)}
//                       className="w-full bg-transparent border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-black focus:ring-0 focus:outline-none"
//                     />
//                     <input
//                       type="text"
//                       placeholder="Meta Title"
//                       value={metaTitle}
//                       onChange={(e) => setMetaTitle(e.target.value)}
//                       className="w-full bg-transparent border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-black focus:ring-0 focus:outline-none"
//                     />
//                     <textarea
//                       placeholder="Meta Description"
//                       value={metaDescription}
//                       onChange={(e) => setMetaDescription(e.target.value)}
//                       rows={3}
//                       className="w-full bg-transparent border border-gray-300 rounded-md px-3 py-2 outline-none resize-none focus:border-black focus:ring-0 focus:outline-none"
//                     />
//                     <textarea
//                       type="text"
//                       placeholder="Keywords"
//                       value={keywords}
//                       onChange={(e) => setKeywords(e.target.value)}
//                       className="w-full bg-transparent border border-gray-300 rounded-md px-3 py-2 outline-none resize-none focus:border-black focus:ring-0 focus:outline-none"
//                     />
//                   </div>

//                   <h3 className="text-lg font-semibold my-6 text-left tracking-wide">
//                     Additional Data
//                   </h3>
//                   <div className="grid grid-cols-2 gap-5 text-left">
//                     <input
//                       type="date"
//                       className="w-full bg-transparent border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-black focus:ring-0 focus:outline-none"
//                     />
//                     <input
//                       type="text"
//                       placeholder="Read Time"
//                       className="w-full bg-transparent border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-black focus:ring-0 focus:outline-none"
//                     />
//                     <textarea
//                       placeholder="Location"
//                       rows={2}
//                       className="w-full bg-transparent border border-gray-300 rounded-md px-3 py-2 outline-none resize-none focus:border-black focus:ring-0 focus:outline-none"
//                     />
//                   </div>
//                 </div>

//                 <div className="w-full flex flex-col">
//                   <input
//                     ref={coverInputRef}
//                     type="file"
//                     id="cover-upload"
//                     className="hidden"
//                     accept="image/webp"
//                     onChange={(e) => handleCoverThumb(e.target.files[0])}
//                   />

//                   <div
//                     onClick={() => coverInputRef.current.click()}
//                     className="w-full h-[300px] border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center bg-white cursor-pointer transition relative overflow-hidden"
//                   >
//                     {coverThumb ? (
//                       <img
//                         src={coverThumb}
//                         alt="Cover Thumbnail"
//                         className="w-full h-full object-cover"
//                       />
//                     ) : (
//                       <div className="flex flex-col items-center text-center px-4">
//                         <svg
//                           width="50"
//                           height="50"
//                           viewBox="0 0 24 24"
//                           fill="none"
//                           className="mb-2"
//                         >
//                           <rect
//                             x="2"
//                             y="4"
//                             width="16"
//                             height="13"
//                             rx="2"
//                             stroke="black"
//                             strokeWidth="1.5"
//                           />
//                           <circle cx="6.5" cy="8.5" r="1.5" fill="black" />
//                           <path
//                             d="M2 13L6 9L11 14L14 11L18 15"
//                             stroke="black"
//                             strokeWidth="1.5"
//                           />
//                           <circle
//                             cx="17"
//                             cy="17"
//                             r="6"
//                             fill="white"
//                             stroke="black"
//                             strokeWidth="1.5"
//                           />
//                           <path
//                             d="M17 14V20M14 17H20"
//                             stroke="black"
//                             strokeWidth="1.5"
//                           />
//                         </svg>
//                         <p className="text-gray-600 text-sm font-medium">
//                           Upload Cover Thumbnail (webp)
//                         </p>
//                       </div>
//                     )}
//                   </div>

//                   <div className="flex justify-end mt-3">
//                     <button
//                       type="button"
//                       onClick={() => coverInputRef.current.click()}
//                       className="flex items-center gap-2 border border-gray-300 px-4 py-1 rounded-lg shadow-sm hover:bg-gray-50 transition-all"
//                     >
//                       <svg
//                         width="21"
//                         height="21"
//                         viewBox="0 0 24 24"
//                         fill="none"
//                       >
//                         <rect
//                           x="2"
//                           y="4"
//                           width="16"
//                           height="13"
//                           rx="2"
//                           stroke="black"
//                           strokeWidth="1.5"
//                         />
//                         <circle cx="6.5" cy="8.5" r="1.5" fill="black" />
//                         <path
//                           d="M2 13L6 9L11 14L14 11L18 15"
//                           stroke="black"
//                           strokeWidth="1.5"
//                         />
//                         <circle
//                           cx="17"
//                           cy="17"
//                           r="6"
//                           fill="white"
//                           stroke="black"
//                           strokeWidth="1.5"
//                         />
//                         <path
//                           d="M17 14V20M14 17H20"
//                           stroke="black"
//                           strokeWidth="1.5"
//                         />
//                       </svg>

//                       <span className="text-sm font-semibold">
//                         {coverThumb ? "Replace" : "Add"}
//                       </span>
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* BUTTONS */}
//             <div className="grid grid-cols-2 gap-3 mt-8 w-80 justify-self-center">
//               <button
//                 onClick={handleCancel}
//                 className="px-6 z-30 py-2 bg-gray-800 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-gray-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
//               >
//                 Cancel
//               </button>

//               <button
//                 onClick={handleSave}
//                 className="px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
//               >
//                 Save
//               </button>
//             </div>
//           </div>
//         </div>
//       </main>
//     </div>
//   );
// };

// export default BlogElementor;
